import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
  FileText, 
  Flame,
  Clock,
  User,
  Building,
  StickyNote,
  Tag,
  Calendar
} from 'lucide-react';
import { ReportFilters } from '../../types/reports';
import { useQuery } from '@tanstack/react-query';
import api from '../../lib/api';
import { useWorkspaceReports } from '../../hooks/useWorkspaceReports';
import Chip from '../Chip';
import SponsorDisplay from '../SponsorDisplay';
import { getCommitteeNameShort } from '../../utils/committeeUtils';
import { getPositionColorClasses } from '../../utils/positionColors';
import { hasUpcomingCalendarEvent } from '../../utils/calendarUtils';
import { getBillLinkClasses } from '../../utils/linkStyles';
import BillTable from '../shared/BillTable';
import { Bill } from '../../types/bill';
import ViewNotesModal from '../notes/ViewNotesModal';
import TruncatedText from '../TruncatedText';
import ReportLogoHeader from './ReportLogoHeader';
import { WorkspaceBadgeList } from '../WorkspaceBadge';
import CalendarEventDisplay from '../shared/CalendarEventDisplay';
import LoadingList from '@shared/ui/LoadingList';
import ExportMenu from '../ExportMenu';
import ViewToggle from '../ViewToggle';
import { formatDate } from '../../utils/dateUtils';

// Helper functions for card view
const normalizeSubject = (subject: string | string[] | undefined): string[] => {
  if (!subject) return [];
  return Array.isArray(subject) ? subject : [subject];
};


// Parse a calendar reading_date that may arrive as a JS Date object, ISO string, or "YYYY-MM-DD HH:MM:SS" timestamp.
// Always returns local-midnight Date so comparisons with today work regardless of timezone.
const parseEventDate = (val: any): Date | null => {
  if (!val) return null;
  if (val instanceof Date) {
    // pg returns DATE columns as Date objects (midnight UTC) — extract YYYY-MM-DD via ISO
    if (isNaN(val.getTime())) return null;
    const iso = val.toISOString(); // "2026-03-15T00:00:00.000Z"
    const [y, m, d] = iso.split('T')[0].split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  const s = String(val);
  const match = s.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
};


interface BillTrackingReportProps {
  workspaceId?: string;
  workspaceName?: string;
  organizationId?: string;
  organizationName?: string;
  filters?: ReportFilters;
  /** Pre-loaded bills data (skips internal fetch — used by public report page). */
  prefetchedBills?: any[];
  /** Custom bill URL builder for public-facing links (e.g. /public/bills/{billNumber}). */
  getBillUrl?: (billNumber: string) => string;
  /** Called when the user switches between card and table view. */
  onViewModeChange?: (mode: 'card' | 'table') => void;
  /** Override export handler (used by public report page to route through public download endpoint). */
  onExport?: (format: 'excel' | 'csv' | 'pdf' | 'word', viewMode: 'card' | 'table') => void;
}

// Stable empty filters object to prevent unnecessary re-renders
const EMPTY_FILTERS: ReportFilters = {};

const BillTrackingReport: React.FC<BillTrackingReportProps> = ({ 
  workspaceId,
  workspaceName = 'Workspace',
  organizationId,
  organizationName,
  filters,
  prefetchedBills,
  getBillUrl,
  onViewModeChange,
  onExport,
}) => {
  const isOrgMode = !!organizationId;
  const displayName = isOrgMode ? (organizationName || 'Organization') : workspaceName;
  // Use stable empty object if no filters provided (prevents re-renders)
  const stableFilters = filters || EMPTY_FILTERS;
  const [viewMode, setViewMode] = useState<'table' | 'card'>('card');
  const [sortField, setSortField] = useState<string>('billNumber');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [selectedBillNotes, setSelectedBillNotes] = useState<{
    billId: string;
    billNumber: string;
    billTitle: string;
    notes: Array<{
      id: string;
      title: string;
      content: string;
      author_name: string;
      created_at: string;
      updated_at: string;
      include_in_reports: boolean;
      tags: string[];
      parent_note_id?: string;
      reply_count: number;
    }>;
  } | null>(null);

  // Fetch tracked bills data with notes support from workspace or org reports.
  // Skipped when prefetchedBills is provided (e.g. from public report page).
  const { data: billsData = [], isLoading: isFetching } = useQuery(
    isOrgMode ? ['org-bill-tracking-data', organizationId] : ['workspace-bill-tracking-data', workspaceId],
    async () => {
      const url = isOrgMode
        ? `/org-reports/${organizationId}/bill-tracking-data`
        : `/workspace-reports/${workspaceId}/bill-tracking-data`;
      const response = await api.get(url);
      if (!response.data.success) {
        throw new Error('Failed to fetch bills data');
      }
      return response.data.data.bills || [];
    },
    {
      enabled: prefetchedBills === undefined && (isOrgMode ? !!organizationId : !!workspaceId),
      staleTime: 30000
    }
  );

  const isLoading = prefetchedBills === undefined && isFetching;

  // Ensure every bill has nextCalendarEvent set (BillTable calendar column uses this).
  // The org/public endpoint returns calendarEvents[] but not nextCalendarEvent, so we
  // derive it here by picking the earliest upcoming event from the array.
  const bills = useMemo(() => {
    const rawBills = prefetchedBills !== undefined
      ? (Array.isArray(prefetchedBills) ? prefetchedBills : [])
      : (Array.isArray(billsData) ? billsData : []);
    return rawBills.map((bill: any) => {
      if (bill.nextCalendarEvent != null || !bill.calendarEvents?.length) return bill;
      const today = new Date(new Date().toDateString());
      const upcoming = (bill.calendarEvents as any[])
        .map((e: any) => ({ e, d: parseEventDate(e.reading_date ?? e.date ?? e.event_date) }))
        .filter(({ d }) => d !== null && d >= today)
        .sort((a, b) => a.d!.getTime() - b.d!.getTime())[0];
      if (!upcoming) return bill;
      const dateStr = (() => {
        const raw = upcoming.e.reading_date ?? upcoming.e.date ?? '';
        if (raw instanceof Date) return raw.toISOString().split('T')[0];
        const m = String(raw).match(/(\d{4}-\d{2}-\d{2})/);
        return m ? m[1] : '';
      })();
      return {
        ...bill,
        nextCalendarEvent: {
          calendar_id: upcoming.e.calendar_id,
          reading_date: dateStr,
          reading_time: upcoming.e.reading_time,
          room_number: upcoming.e.room_number,
          chamber: upcoming.e.chamber,
          upon_adjournment: upcoming.e.upon_adjournment,
        }
      };
    });
  }, [prefetchedBills, billsData]);

  const total = bills.length;

  // Sort bills based on current sort settings
  const sortBills = (billsToSort: any[]) => {
    return [...billsToSort].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'billNumber':
        case 'number':
          aValue = a.billNumber || '';
          bValue = b.billNumber || '';
          break;
        case 'status':
          aValue = a.status || '';
          bValue = b.status || '';
          break;
        case 'billPosition':
          aValue = a.billPosition || '';
          bValue = b.billPosition || '';
          break;
        case 'introducedDate':
          aValue = a.introducedDate || '';
          bValue = b.introducedDate || '';
          break;
        case 'lastActivity':
          aValue = a.lastActivity || '';
          bValue = b.lastActivity || '';
          break;
        case 'houseCommittees':
          aValue = a.committees?.filter((c: any) => c.chamber?.toLowerCase() === 'house').length || 0;
          bValue = b.committees?.filter((c: any) => c.chamber?.toLowerCase() === 'house').length || 0;
          break;
        case 'senateCommittees':
          aValue = a.committees?.filter((c: any) => c.chamber?.toLowerCase() === 'senate').length || 0;
          bValue = b.committees?.filter((c: any) => c.chamber?.toLowerCase() === 'senate').length || 0;
          break;
        case 'calendarDate': {
          // Sort by calendar date: records with events come first, then sorted by date/time
          const aEvents = a.calendarEvents || [];
          const bEvents = b.calendarEvents || [];
          const aHasEvent = aEvents.length > 0;
          const bHasEvent = bEvents.length > 0;
          
          // First: records with events come before records without
          if (aHasEvent && !bHasEvent) return -1;
          if (!aHasEvent && bHasEvent) return 1;
          
          // If both have no events, they're equal for calendar sorting
          if (!aHasEvent && !bHasEvent) return 0;
          
          // Both have events - sort by the first event's date/time
          const aDate = aEvents[0]?.date || aEvents[0]?.reading_date || '';
          const bDate = bEvents[0]?.date || bEvents[0]?.reading_date || '';
          const aTime = aEvents[0]?.time || aEvents[0]?.reading_time || '00:00:00';
          const bTime = bEvents[0]?.time || bEvents[0]?.reading_time || '00:00:00';
          
          const aDateTime = new Date(`${aDate}T${aTime}`);
          const bDateTime = new Date(`${bDate}T${bTime}`);
          
          if (aDateTime < bDateTime) return sortDirection === 'asc' ? -1 : 1;
          if (aDateTime > bDateTime) return sortDirection === 'asc' ? 1 : -1;
          return 0;
        }
        default:
          return 0;
      }

      // Handle null/undefined values
      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';

      // Compare values
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      } else {
        const stringA = String(aValue).toLowerCase();
        const stringB = String(bValue).toLowerCase();
        const comparison = stringA.localeCompare(stringB);
        return sortDirection === 'asc' ? comparison : -comparison;
      }
    });
  };

  const sortedBills = sortBills(bills);

  // Org mode: builder already returns one bill per unique bill with workspaces[] populated.
  // No client-side deduplication needed.

  const { exportReport } = useWorkspaceReports(workspaceId, organizationId);

  const handleExport = async (format: 'excel' | 'csv' | 'pdf' | 'word') => {
    try {
      if (onExport) {
        // Public page: delegate to parent which routes through the public download endpoint.
        await onExport(format, viewMode);
      } else {
        // Authenticated page: use the standard workspace/org export endpoint.
        await exportReport('bill-tracking', format as any, {
          ...stableFilters, 
          viewMode,
          includeFullDetails: viewMode === 'card'
        } as any);
      }
    } catch (err: any) {
      console.error('Export failed:', err);
    }
  };


  if (isLoading) {
    return (
      <LoadingList rows={5} label="Loading report" />
    );
  }

  if (sortedBills.length === 0) {
    return (
      <div className="text-center py-8 text-text-tertiary dark:text-text-dark-tertiary">
        <FileText className="h-12 w-12 mx-auto mb-4 text-text-tertiary dark:text-text-dark-tertiary" />
        <h3 className="text-lg font-medium text-text-primary dark:text-text-dark-primary mb-2">
          No tracked bills found
        </h3>
        <p className="text-text-secondary dark:text-text-dark-secondary">
          No bills are currently being tracked in this workspace.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Report Logo Header — only shown in authenticated (non-prefetched) context */}
      {prefetchedBills === undefined && <ReportLogoHeader workspaceId={workspaceId} />}
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h3 className="text-lg font-semibold text-left text-text-primary dark:text-text-dark-primary flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary-600" />
            {prefetchedBills !== undefined ? 'Bills Report' : `${displayName} - Bills Report`}
          </h3>
          <p className="text-sm text-left text-text-secondary dark:text-text-dark-secondary mt-1">
            All bills being tracked{isOrgMode ? ' across all workspaces' : ' in this workspace'}
          </p>
        </div>
        
        <div className="flex items-center gap-4">
          {/* Compact Summary */}
          <div className="flex items-center space-x-4 text-sm text-text-secondary dark:text-text-dark-secondary">
            <span><strong className="text-primary-600">{total || 0}</strong> {isOrgMode ? 'Unique Bills' : 'Total Bills'}</span>
            <span><strong className="text-error-600">{sortedBills.filter(bill => bill.isHot).length}</strong> Hot Bills</span>
          </div>
          
          {/* View Toggle */}
          <ViewToggle
            value={viewMode}
            onChange={(mode) => {
              setViewMode(mode);
              onViewModeChange?.(mode);
            }}
          />
          
          {/* Export Dropdown — hidden when pre-loaded (public page has its own export) */}
          <ExportMenu
            formats={viewMode === 'table' ? ['csv', 'excel', { format: 'pdf', note: 'Landscape' }] : [{ format: 'word', note: 'Portrait' }, { format: 'pdf', note: 'Portrait' }]}
            onExport={(format) => handleExport(format)}
          />
        </div>
      </div>


      {/* Bills Display */}
      <div className="card p-0 overflow-hidden">
        {viewMode === 'table' ? (
            <BillTable
              bills={sortedBills as Bill[]}
              context={isOrgMode ? 'org-bill-tracking-report' : 'bill-tracking-report'}
              workspaceId={isOrgMode ? undefined : workspaceId}
              reportMode={true}
              notesDisplayMode="inline"
              sortBy={sortField}
              sortDirection={sortDirection}
              onSort={(field, direction) => {
                setSortField(field);
                setSortDirection(direction);
              }}
              getBillHref={getBillUrl ? (bill) => getBillUrl(bill.billNumber || '') : undefined}
              onBillNavigate={getBillUrl ? (bill) => { window.location.href = getBillUrl(bill.billNumber || ''); } : undefined}
              isPublic={!!getBillUrl}
            />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
            {sortedBills.map((bill) => (
              <div key={bill.id ?? bill.billNumber} className="card p-4 hover:shadow-md transition-shadow">
                {/* Bill Header */}
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    {bill.isHot && <Flame className="w-4 h-4 text-error-500" />}
                    {getBillUrl ? (
                      <a href={getBillUrl(bill.billNumber || '')} target="_blank" rel="noopener noreferrer">
                        <h3 className={`text-lg ${getBillLinkClasses()}`}>{bill.billNumber}</h3>
                      </a>
                    ) : (
                      <Link to={`/legislation/${bill.id}`}>
                        <h3 className={`text-lg ${getBillLinkClasses()}`}>
                          {bill.billNumber}
                        </h3>
                      </Link>
                    )}
                  </div>
                  {/* Workspace mode: single position chip stays in header */}
                  {!isOrgMode && bill.billPosition && (
                    <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${getPositionColorClasses(bill.billPosition)}`}>
                      {bill.billPosition}
                    </span>
                  )}
                </div>
                {/* Org mode: workspace badges as own row below header */}
                {isOrgMode && bill.workspaces && bill.workspaces.length > 0 && (
                  <WorkspaceBadgeList workspaces={bill.workspaces} className="mb-3" />
                )}

                {/* Title */}
                <div className="mb-3">
                  <TruncatedText 
                    text={bill.title} 
                    lines={2}
                    className="font-medium"
                  />
                </div>

                {/* Description */}
                {bill.concerning && (
                  <div className="mb-3">
                    <div className="flex items-start space-x-1">
                      <Tag className="w-4 h-4 text-primary-500 mt-0.5 flex-shrink-0" />
                      <TruncatedText 
                        text={bill.concerning} 
                        lines={2}
                        className="text-sm text-text-secondary dark:text-text-dark-secondary"
                      />
                    </div>
                  </div>
                )}

                {/* Summary */}
                {bill.summary && (
                  <div className="mb-3">
                    <TruncatedText 
                      text={bill.summary} 
                      lines={3}
                      className="text-sm text-text-secondary dark:text-text-dark-secondary"
                    />
                  </div>
                )}

                {/* Details Grid */}
                <div className="space-y-2 text-sm text-text-secondary dark:text-text-dark-secondary">
                  {/* Intro Date */}
                  {bill.introducedDate && (
                    <div className="flex items-center space-x-1">
                      <Clock className="w-4 h-4" />
                      <span>Introduced: {formatDate(bill.introducedDate)}</span>
                    </div>
                  )}

                  {/* Subject */}
                  {bill.subject && bill.subject.length > 0 && (
                    <div className="flex items-start space-x-1">
                      <Tag className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <div className="flex flex-wrap gap-1">
                        {normalizeSubject(bill.subject).slice(0, 3).map((subj: string, index: number) => (
                          <Chip
                            key={index}
                            label={subj}
                            variant="custom"
                            className="text-xs bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-300"
                          />
                        ))}
                        {bill.subject.length > 3 && (
                          <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
                            +{bill.subject.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Sponsors */}
                  {bill.sponsors && bill.sponsors.length > 0 && (
                    <div className="flex items-start space-x-1">
                      <User className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <div className="text-xs">
                        <SponsorDisplay sponsors={bill.sponsors} billNumber={bill.billNumber} isPublic={!!getBillUrl} />
                      </div>
                    </div>
                  )}

                  {/* House Committees */}
                  {(() => {
                    const houseCommittees = bill.committees?.filter((c: any) => c.chamber?.toLowerCase() === 'house') || [];
                    if (houseCommittees.length === 0) return null;
                    
                    return (
                      <div className="flex items-start space-x-1">
                        <Building className="w-4 h-4 mt-0.5 flex-shrink-0 text-primary-500" />
                        <div className="text-xs">
                          <span className="font-medium text-text-secondary dark:text-text-dark-secondary">House: </span>
                          <span className="text-text-secondary dark:text-text-dark-secondary">
                            {houseCommittees.map((c: any) => getCommitteeNameShort(c.name)).join(', ')}
                          </span>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Senate Committees */}
                  {(() => {
                    const senateCommittees = bill.committees?.filter((c: any) => c.chamber?.toLowerCase() === 'senate') || [];
                    if (senateCommittees.length === 0) return null;
                    
                    return (
                      <div className="flex items-start space-x-1">
                        <Building className="w-4 h-4 mt-0.5 flex-shrink-0 text-info-500" />
                        <div className="text-xs">
                          <span className="font-medium text-text-secondary dark:text-text-dark-secondary">Senate: </span>
                          <span className="text-text-secondary dark:text-text-dark-secondary">
                            {senateCommittees.map((c: any) => getCommitteeNameShort(c.name)).join(', ')}
                          </span>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Notes */}
                  {bill.notes && Array.isArray(bill.notes) && bill.notes.length > 0 && (
                    <div 
                      className="flex items-start space-x-1 cursor-pointer hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary rounded p-1 -m-1 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedBillNotes({
                          billId: bill.id?.toString() || '',
                          billNumber: bill.billNumber,
                          billTitle: bill.title || 'No title',
                          notes: bill.notes.map((n: any) => ({
                            id: n.id,
                            title: n.title,
                            content: n.content,
                            author_name: n.author,
                            created_at: n.created_at,
                            updated_at: n.created_at,
                            include_in_reports: true,
                            tags: n.tags || [],
                            parent_note_id: undefined,
                            reply_count: 0
                          }))
                        });
                      }}
                    >
                      <StickyNote className="w-4 h-4 text-warning-500 mt-0.5 flex-shrink-0" />
                      <div className="text-xs text-text-secondary dark:text-text-dark-secondary">
                        {bill.notes.slice(0, 2).map((note: any, idx: number) => (
                          <div key={idx} className="mb-1 last:mb-0">
                            {note.title && <span className="font-medium">{note.title}: </span>}
                            <span 
                              className="line-clamp-1 prose prose-xs max-w-none"
                              dangerouslySetInnerHTML={{ __html: note.content }}
                            />
                          </div>
                        ))}
                        {bill.notes.length > 2 && (
                          <span className="text-text-tertiary dark:text-text-dark-tertiary">+{bill.notes.length - 2} more notes (click to view all)</span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Calendar Events - Highlighted box (only show if has upcoming events) */}
                  {hasUpcomingCalendarEvent(bill.calendarEvents) && (
                    <div className="mt-3 p-2 bg-purple-50 dark:bg-purple-900/20 rounded border border-purple-200 dark:border-purple-800">
                      <div className="flex items-center gap-1 mb-1">
                        <Calendar className="w-3 h-3 text-purple-600 dark:text-purple-400" />
                        <span className="text-xs font-medium text-purple-900 dark:text-purple-300">Calendar Date</span>
                      </div>
                      <CalendarEventDisplay
                        events={bill.calendarEvents}
                        billNumber={bill.billNumber}
                        billTitle={bill.title}
                        variant="inline"
                        maxDisplay={2}
                        context={getBillUrl ? 'public' : 'authenticated'}
                        showIcon={false}
                        showLabel={true}
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* View Notes Modal */}
      {selectedBillNotes && (
        <ViewNotesModal
          isOpen={!!selectedBillNotes}
          onClose={() => setSelectedBillNotes(null)}
          notes={selectedBillNotes.notes}
          entityType="bill"
          entityId={selectedBillNotes.billId}
          entityDisplayName={`${selectedBillNotes.billNumber || 'Unknown'} - ${selectedBillNotes.billTitle || 'No title'}`}
          workspaceId={workspaceId}
        />
      )}
    </div>
  );
};

export default BillTrackingReport;
